import React, { useEffect, useRef } from 'react';
import { GenerateMap } from '../components/storerocket';
import Layout from '../components/layout';
import Products from '../components/products';
import HeroBanner from '../images/fire-pit-and-wood-stack.jpg';
import ThrowingWood from '../images/throwing-wood-into-fire-pit.jpg';
import FirewoodStack from '../images/firewood-stack.jpg';
import HowToStartAFire from '../images/how-to-start-a-fire.jpg';
import LetsTalkWood from '../images/lets-talk-wood.jpg';
import FirewoodStackDark from '../images/firewood-stack-dark.jpg';
import IconBinoculars from '../images/svg/icon-binoculars.svg';
import IconBreeoPit from '../images/svg/icon-breeo-pit.svg';
import IconCansOnString from '../images/svg/icon-cans-on-string.svg';
import IconCheers from '../images/svg/icon-cheers.svg';
import IconFirewoodStack from '../images/svg/icon-firewood-stack.svg';

const IndexPage = () => {
    const mapRef = useRef(null);

    // StoreRocket
    useEffect(() => {
        if (!mapRef.current) {
            return;
        }

        const currentMapRef = mapRef.current;

        if (typeof window.StoreRocket !== 'undefined') {
            GenerateMap({
                mapRef: currentMapRef
            });
        } else {
            window.addEventListener('StoreRocketLoaded', () => {
                GenerateMap({
                    mapRef: currentMapRef
                });
            });
        }
    }, [mapRef]);

    return (
        <Layout>
            <div className="relative flex justify-center items-center pt-16 pb-24 text-center md:pt-32 md:pb-48">
                <img
                    src={HeroBanner}
                    alt=""
                    className="absolute inset-0 object-cover h-full w-full"
                />
                <div className="absolute inset-0 h-full w-full bg-charcoal/50"></div>
                <div className="container-lg relative w-full text-white">
                    <h1 className="text-inherit leading-none md:mb-12">
                        Find Wood. Get Wood. Burn Wood.
                    </h1>
                    <p className="md:text-lg">
                        We want to help make sure you have a good stack of wood
                        on hand when you want to kick back by the fire as the
                        sun goes down. This tool allows you to search by region
                        and share sources so that you and all the other
                        firemasters out there are stocked up when the desire to
                        burn is real.
                    </p>
                </div>
            </div>
            <div className="container -mt-16 mb-8 md:-mt-32">
                <div
                    id="supplier-map"
                    ref={mapRef}
                    className="relative flex items-center bg-light min-h-[498px]"
                ></div>
                <p className="mt-8 text-sm text-center">
                    <em>
                        We strongly recommend buying your firewood locally. Not
                        only are you supporting your local economy, you are
                        protecting the trees around you from the spread of
                        invasive species and disease.
                    </em>
                </p>
            </div>
            <div className="section relative">
                <div className="container">
                    <img
                        src={FirewoodStackDark}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="relative flex flex-wrap justify-center gap-4 font-display text-white uppercase md:flex-nowrap md:gap-16">
                        <div className="flex flex-col items-center w-1/4 md:w-1/5">
                            <img
                                src={IconBinoculars}
                                alt=""
                                className="block h-20 md:h-auto mb-2"
                            />
                            <div>Search</div>
                        </div>
                        <div className="flex flex-col items-center w-1/4 md:w-1/5">
                            <img
                                src={IconCansOnString}
                                alt=""
                                className="block h-20 md:h-auto mb-2"
                            />
                            <div>Order</div>
                        </div>
                        <div className="flex flex-col items-center w-1/4 md:w-1/5">
                            <img
                                src={IconFirewoodStack}
                                alt=""
                                className="block h-20 md:h-auto mb-2"
                            />
                            <div>Stack</div>
                        </div>
                        <div className="flex flex-col items-center w-1/4 md:w-1/5">
                            <img
                                src={IconBreeoPit}
                                alt=""
                                className="block h-20 md:h-auto mb-2"
                            />
                            <div>Burn</div>
                        </div>
                        <div className="flex flex-col items-center w-1/4 md:w-1/5">
                            <img
                                src={IconCheers}
                                alt=""
                                className="block h-20 md:h-auto mb-2"
                            />
                            <div>Cheers!</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container section grid gap-8 md:gap-16">
                <div className="grid gap-8 md:grid-cols-2 md:gap-16">
                    <div>
                        <img
                            src={ThrowingWood}
                            alt="Hands throwing wood into a Breeo fire pit"
                        />
                    </div>
                    <div className="self-center">
                        <h2>Hardwood is best</h2>
                        <p>
                            We recommend looking for kiln dried or properly
                            seasoned hardwoods like Oak, Hickory, Walnut, Ash,
                            and Birch. A blend of these is often referred to as
                            “Mixed Hardwood” by suppliers, and work especially
                            well for cooking. Cherry, Maple, Birch, and Elm are
                            still good choices but not as dense as the others.
                            Softwoods like Pine, Spruce, Hemlock, Aspen, and
                            Cedar will burn much faster and are often used for
                            kindling.
                        </p>
                    </div>
                </div>
                <div className="grid gap-8 md:grid-cols-2 md:gap-16">
                    <div>
                        <img src={FirewoodStack} alt="Firewood stack" />
                    </div>
                    <div className="self-center order-auto md:-order-1">
                        <h2>How much to order?</h2>
                        <p>
                            The standard measurement of split firewood is a
                            “cord” which is 128 cubic ft or 4’ x 4’ x 8’
                            stacked. Most order a full or half cord at a time.
                            Be sure to check with the supplier on the length of
                            the logs, you want to make sure they’ll fit in your
                            fire pit. Be aware of “face cords”, this stack
                            measures 4’ x 8’ but is only as deep as the length
                            of the log (usually 16”-18”), which equals about ⅓
                            of a full cord.
                        </p>
                    </div>
                </div>
            </div>
            <div className="container section grid gap-8 md:gap-16">
                <div className="grid gap-8 md:grid-cols-2 md:gap-16">
                    <a
                        href="https://breeo.co/blogs/how-to/the-ultimate-guide-to-building-a-fire"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div className="mb-8">
                            <img src={HowToStartAFire} alt="" />
                        </div>
                        <div className="self-center order-auto md:-order-1">
                            <h3>How to start a fire: The Ultimate Guide</h3>
                            <p>
                                Whether you just got your Breeo® fire pit or you
                                need a refresher on the basics of smokeless
                                fire, this guide is going to help you take steps
                                to becoming the firemaster.
                            </p>
                        </div>
                    </a>
                    <a
                        href="https://breeo.co/blogs/info/lets-talk-wood"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div className="mb-8">
                            <img src={LetsTalkWood} alt="" />
                        </div>
                        <div className="self-center order-auto md:-order-1">
                            <h3>Let's Talk Wood</h3>
                            <p>
                                When cooking over live fire, picking the right
                                fuel source is as important as choosing what
                                food to prepare. The fuel you select will impact
                                the final flavor of your meal and even plays a
                                role in cleanup.
                            </p>
                        </div>
                    </a>
                </div>
            </div>
            <Products />
        </Layout>
    );
};

export default IndexPage;
